import { ChangeDetectorRef, Directive, HostBinding, Input, OnDestroy, Signal, ViewContainerRef, inject } from '@angular/core';
import { DeviceService } from '@core/services/device/device.service';
import { kebabCase } from 'lodash-es';
import { Subject } from 'rxjs';

const getDomTreeLocalNames = (element: HTMLElement) => {
    const names: string[] = [];

    if (element.parentElement?.localName) {
        if (element.parentElement.localName.startsWith('askemo-') && !notIncludedDomTreeLocalNames.includes(element.parentElement.localName)) {
            if (element.parentElement.id) {
                names.unshift(element.parentElement.id ?? element.parentElement.localName);
                return names;
            }

            names.unshift(element.parentElement.localName);
        }

        names.unshift(...getDomTreeLocalNames(element.parentElement));
    }

    return names;
};

const notIncludedDomTreeLocalNames = ['app-root', 'app-private-pages'];

/**
 * Base component to be inherited by every new component of the application.
 *
 * @export
 * @class BaseComponent
 * @implements {OnDestroy}
 */

@Directive()
export class BaseComponent implements OnDestroy {
    public viewContainerRef = inject(ViewContainerRef);
    public readonly cdr = inject(ChangeDetectorRef);
    private readonly deviceService = inject(DeviceService);
    // eslint-disable-next-line @angular-eslint/prefer-signals
    @Input()
    public get parentId(): string {
        return this._parentId;
    }
    public set parentId(value: string) {
        this._parentId = value;
        this._id = null;
    }

    public selector: string;
    public readonly isMobile: Signal<boolean>;
    public destroyed = new Subject<void>();

    private _parentId: string = null;
    private _id: string = null;

    constructor() {
        this.isMobile = this.deviceService.isMobile;
    }

    public ngOnDestroy(): void {
        this.destroyed.next();
        this.destroyed.complete();
    }

    @HostBinding('attr.id')
    public get id(): string {
        if (this._id === null) {
            if (this.viewContainerRef.element.nativeElement?.localName) {
                this.selector = this.viewContainerRef.element.nativeElement.localName;
            }

            if (this.parentId === null) {
                const domTreeLocalNames = getDomTreeLocalNames(this.viewContainerRef.element.nativeElement);
                this.parentId = domTreeLocalNames.join('__');
            }

            this._id = this.parentId ? `${this.parentId}__${this.selector}` : this.selector;
        }

        return this._id;
    }

    /**
     * Gets a new unique id
     *
     * @param name
     * @param [outterIndex=null]
     * @param [innerIndex=null]
     * @returns
     *
     * @memberOf BaseComponent
     */
    public getId(name: string | number, outterIndex: number = null, innerIndex: number = null) {
        return this.id + (outterIndex !== null ? `__${outterIndex}` : '') + (innerIndex !== null ? `__${innerIndex}` : '') + '__' + kebabCase(name as string);
    }

    /**
     * Gets a new unique button id
     *
     * @param name
     * @param [index=null]
     * @returns
     *
     * @memberOf BaseComponent
     */
    public getButtonId(name: string | number, index: number = null) {
        return this.getId(`${name}-button`, index);
    }

    /**
     * Gets a new unique checkbox id
     *
     * @param name
     * @param [index=null]
     * @returns
     *
     * @memberOf BaseComponent
     */
    public getCheckboxId(name: string, index: number = null) {
        return this.getId(`${name}-checkbox`, index);
    }

    /**
     * Gets a new unique radio id
     *
     * @param name
     * @param [index=null]
     * @returns
     *
     * @memberOf BaseComponent
     */
    public getRadioId(name: string, index: number = null) {
        return this.getId(`${name}-radio`, index);
    }

    /**
     * Gets a new unique label id
     *
     * @param name
     * @param [index=null]
     * @returns
     *
     * @memberOf BaseComponent
     */
    public getLabelId(name: string, index: number = null) {
        return this.getId(`${name}-label`, index);
    }

    /**
     * Gets a new unique input id
     *
     * @param name
     * @param [index=null]
     * @returns
     *
     * @memberOf BaseComponent
     */
    public getInputId(name = '', index: number = null) {
        return this.getId(`${name}-input`, index);
    }

    /**
     * Gets a new unique link id
     *
     * @param name
     * @param [index=null]
     * @returns
     *
     * @memberOf BaseComponent
     */
    public getLinkId(name: string, index: number = null) {
        return this.getId(`${name}-link`, index);
    }

    /**
     * Gets a new unique link id
     *
     * @param name
     * @param [index=null]
     * @returns
     *
     * @memberOf BaseComponent
     */
    public getSelectId(name = '', index: number = null) {
        return this.getId(`${name}-select`, index);
    }

    public markForCheck(): void {
        this.cdr.markForCheck();
    }
}
